import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, scrollToId } from "@/lib/site";

const links = [
  { label: "How it Works", href: "#how" },
  { label: "Plans", href: "#plans" },
  { label: "Courses", href: "#courses" },
  { label: "Why Us", href: "#why" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,box-shadow,padding] duration-300 ${
        scrolled ? "bg-background/80 backdrop-blur-xl border-b border-black/5 py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); scrollTo("#top"); }} className="flex items-center gap-2">
          <img src={BRAND.logo} alt="alfabrains logo" className="h-11 w-11 object-contain" />
          <div className="leading-none">
            <span className="font-heading font-black text-xl text-primary">alfa</span>
            <span className="font-heading font-black text-xl text-secondary">brains</span>
            <div className="font-label text-[9px] tracking-[0.25em] uppercase text-muted-foreground mt-0.5">Online Tutoring</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => scrollTo(l.href)}
              className="font-label text-sm font-medium text-foreground/70 hover:text-primary transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("#demo")}
            className="rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-bold hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow] duration-300"
          >
            Book Free Demo
          </button>
        </nav>

        <button className="md:hidden text-primary" data-testid="nav-menu-toggle" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-black/5"
          >
            <div className="px-5 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <button key={l.href} data-testid={`nav-mobile-${l.href.slice(1)}`} onClick={() => scrollTo(l.href)} className="text-left font-label py-2 text-foreground/80">
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-mobile-cta" onClick={() => scrollTo("#demo")} className="rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold mt-2">
                Book Free Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
